import { styled } from '@mui/system';
import { Box, Button, Grid, Typography } from '@mui/material';
import { colors, fonts, fontWeight, VERIFICATION_STATUS } from '../../constants';

const StyledVerificationTitle = styled(Typography)({
  fontWeight: fontWeight.bold, // Font-weight 700
  fontFamily: fonts.primary, // Applying Roboto font
  fontSize: '1.3rem', // Font size 1.3rem
  fontStyle: 'normal', // Default font style
  margin: 0 // Adjust the margin if needed
});

interface MyCardProps {
  doctorVerificationSelectedTab: string; // or another type depending on card.status
  status: string; // or another type depending on card.status
}

const StyledCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'doctorVerificationSelectedTab' && prop !== 'status'
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
})<MyCardProps>(({ theme, doctorVerificationSelectedTab, status }) => ({
  backgroundColor: colors.blueChipBg,
  border: `1px solid ${colors.blueBorderLight}`,
  borderRadius: '0.25rem',
  padding: '1rem 0 0 1rem',
  height: '7rem',
  display: 'grid',
  gridTemplateRows: 'repeat(2, 1fr)',
  flexDirection: 'column',
  '&:hover': {
    cursor: 'pointer'
  },
  ...(doctorVerificationSelectedTab === status && {
    border: `1px solid ${colors.blueDarkText}`,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
  })
}));

const StatsTop = styled(Box)({
  display: 'flex',
  alignSelf: 'start',
  justifyContent: 'start',
  height: '100%',
  gap: '0.5rem',
  borderBottom: `1px solid ${colors.blueBorderLight}`,
  paddingBottom: '6px',
  paddingRight: '3px'
});

const CardCount = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.5rem'
});

const CardLabel = styled(Typography)({
  color: colors.grayMedium,
  height: '2rem',
  fontSize: '1rem',
  fontWeight: 400,
  alignSelf: 'center',
  paddingBottom: '0.5rem'
});

const CardBottom = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'left',
  gap: '8px'
});

const CardIcon = styled(Box)({
  alignSelf: 'center',
  display: 'flex',
  height: '18px',
  width: '18px'
});

const LowerLabel = styled(Typography)({
  fontSize: '0.75rem',
  color: colors.grayBody,
  alignSelf: 'center'
});

const EmptyStateContainer = styled(Box)({
  textAlign: 'center',
  height: '10rem',
  marginTop: '0px !important',
  display: 'flex',
  alignItems: 'center', // Centers vertically
  justifyContent: 'center' // Centers horizontally
});

const EmptyMessage = styled(Typography)({
  color: '#666',
  marginTop: '0px !important',
  fontStyle: 'italic',
  fontSize: '14px'
});
const IndividualGrid = styled(Grid)({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center'
});

const DoctorName = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: fontWeight.medium
});

const VerifyText = styled(Typography)({
  textTransform: 'none',
  fontSize: '0.7rem'
});

const ViewText = styled(Button)<{ doctorVerificationSelectedTab: number }>(
  ({ doctorVerificationSelectedTab }) => ({
    textTransform: 'none',
    backgroundColor: '#ecf1fe',
    color: 'black',
    borderRadius: '1rem',
    paddingLeft: '10px',
    height: '1.5rem',
    paddingRight: '10px',
    fontSize: '0.8rem  !important',
    border: `0.1rem solid ${colors.blueBase}`,
    width:
      doctorVerificationSelectedTab === 2 || doctorVerificationSelectedTab === 3 ? '5rem' : '3rem',
    textAlign: 'center',
    '&:hover': {
      cursor: 'pointer'
    }
  })
);

const RejectButtonWrapper = styled(Box)({
  height: '4rem',
  display: 'flex',
  paddingRight: '1rem',
  alignItems: 'center',
  justifyContent: 'flex-end',
  borderTop: '1px solid #cccccc',
  '&:hover': {
    cursor: 'pointer'
  }
});

const RejectButton = styled(Button)({
  border: `2px solid  ${colors.redDark}`,
  backgroundColor: colors.redDark,
  fontSize: '14px',
  color: colors.white,
  borderRadius: '8px',
  textTransform: 'unset',
  lineHeight: 'normal',
  '&:hover': {
    backgroundColor: colors.redDark, // Keeps the background color the same
    cursor: 'pointer'
  }
});

const IconLeftWrapper = styled(Box)({
  display: 'flex',
  height: '2.25rem',
  width: '2.25rem',
  backgroundColor: colors.blueBase,
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '0.5rem',
  '&:hover': {
    cursor: 'pointer'
  }
});

const IconLeft = styled(Box)({
  paddingBottom: '0.1rem',
  height: '100vh',
  display: 'flex',
  alignItems: 'flex-start',
  paddingRight: '1rem'
});

// const FeedbackText = styled(Button)(() => ({
//   fontSize: '0.7rem',
//   color: colors.black,
//   textTransform: 'none'
// }));

const getIconColor = (overallStatus: string): string => {
  switch (overallStatus) {
    case VERIFICATION_STATUS.REJECTED:
      return colors.white; // Color for REJECTED
    case VERIFICATION_STATUS.VERIFIED:
      return colors.white; // Color for VERIFIED
    case VERIFICATION_STATUS.UPDATE_REQUESTED:
      return colors.white; // Color for UPDATE_REQUESTED
    default:
      return colors.black; // Default icon color
  }
};

const RejectedText = styled(Typography)<{ overallStatus: string }>(({ overallStatus }) => {
  let color;

  // Set color based on the status
  switch (overallStatus) {
    case VERIFICATION_STATUS.REJECTED:
      color = colors.white; // Example color for REJECTED
      break;
    case VERIFICATION_STATUS.VERIFIED:
      color = colors.white; // Example color for VERIFIED
      break;
    case VERIFICATION_STATUS.UPDATE_REQUESTED:
      color = colors.white; // Example color for UPDATE_REQUESTED
      break;
    default:
      color = colors.black; // Default color
  }

  return {
    fontSize: '0.7rem',
    color,
    textTransform: 'none'
  };
});

const TimelineGrid = styled(Grid)(() => ({
  color: '#595959',
  height: '2.25rem',
  alignSelf: 'center',
  gap: '0.15rem',
  display: 'flex',
  flexDirection: 'column' // Avoid using !important in styled
}));

const DoctorIdText = styled(Typography)(() => ({
  fontSize: '0.65rem'
}));

const ChatButtonWrapper = styled(Box)(() => ({
  display: 'flex',
  height: '2.25rem',
  width: '2.25rem',
  backgroundColor: '#3f73f5',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '0.5rem',
  '&:hover': {
    backgroundColor: colors.blueBase,
    color: 'inherit !important',
    boxShadow: 'none !important'
  }
}));

const RegistrationContent = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  paddingBottom: '0.5rem'
}));

const DetailContent = styled(Typography)(() => ({
  fontSize: '0.9rem',
  color: colors.grayBody,
  fontWeight: fontWeight.large
}));

const BasicDetailsTitle = styled(Typography)(() => ({
  margin: '0rem',
  fontSize: '1rem',
  fontWeight: fontWeight.regular,
  marginBottom: '10px',
  color: colors.grayTitle
}));

const BasicDetailsTitleModal = styled(Typography)(() => ({
  paddingLeft: '2rem',
  margin: '0rem',
  fontSize: '1rem',
  fontWeight: 400,
  marginBottom: '10px',
  color: colors.grayTitle
}));

const BasicDetailsModalSubTitle = styled(Typography)(() => ({
  paddingLeft: '3rem',
  color: colors.grayBody,
  fontWeight: fontWeight.regular,
  marginBottom: '4px',
  fontSize: '0.75rem'
}));

const BasicDetailsModalContent = styled(Typography)(() => ({
  paddingLeft: '3rem',
  fontWeight: fontWeight.large,
  color: colors.grayBody,
  fontSize: '1rem'
}));

const BasicDetailsGrid = styled(Grid)(() => ({
  paddingTop: '1rem'
}));

const DocumentImage = styled('img')({
  width: '280px',
  // height: '250px !important',
  borderRadius: '0.25rem',
  paddingTop: '0.5rem',
  objectFit: 'cover'
});

const AskForUpdateText = styled(Typography)({
  color: colors.iconChevronDown,
  fontSize: '0.7rem'
});

const VerifyUpdateButtons = styled(Grid)({
  paddingBottom: '0.5rem',
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '3rem'
});
const StyledVerifyIconWrapper = styled(Box)({
  display: 'flex',
  height: '2.25rem',
  width: '2.25rem',
  backgroundColor: '#3f73f5',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '0.5rem'
});

const AskForUpdateButton = styled(Button)({
  backgroundColor: colors.blueChipBg,
  color: 'black',
  padding: '0.7rem',
  borderRadius: '0.5rem',
  border: `0.07rem solid ${colors.blueBase}`,
  textTransform: 'none',
  height: '1.75rem',
  '&:hover': {
    backgroundColor: colors.blueChipBg, // Keeps the background color the same
    cursor: 'pointer' // Ensures pointer cursor on hover
  }
});

const VerifyButton = styled(Button)({
  backgroundColor: colors.blueBase,
  color: colors.white,
  borderRadius: '0.5rem',
  height: '1.75rem',
  alignSelf: 'flex-end',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: colors.blueBase // Keeps the background color the same
  }
});

const VerifyButtonWrapper = styled(Grid)({
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  display: 'flex'
});

const DocumentExpiration = styled(Grid)({
  paddingLeft: '40px'
});

const SectionTitle = styled(Typography)({
  margin: '0rem',
  fontSize: '1rem',
  fontWeight: fontWeight.regular,
  marginBottom: '10px',
  color: colors.grayTitle
});

const BasicDetailsTitleModalDegree = styled(Typography)({
  margin: '0rem',
  fontSize: '1rem',
  fontWeight: fontWeight.regular,
  marginBottom: '10px',
  color: colors.grayTitle
});

const MedicalDocTitle = styled(Typography)({
  color: colors.grayBody,
  fontSize: '0.7rem'
});

const MedicalDocsModal = styled(Grid)({
  color: colors.grayBody,
  paddingLeft: '1.5rem',
  paddingRight: '1rem',
  paddingBottom: '1.5rem',
  height: '316px',
  width: '680px'
});

const ExpiryDate = styled(Typography)({
  fontWeight: fontWeight.large,
  fontSize: '1rem'
});

const RejectProfileWrapper = styled(Grid)({
  display: 'flex',
  gap: '1rem',
  justifyContent: 'flex-end'
});

const FeedbackLogButton = styled(Button)({
  backgroundColor: colors.blueChipBg,
  padding: '0.7rem',
  borderRadius: '0.5rem',
  border: `0.07rem solid ${colors.blueBase}`,
  textTransform: 'none',
  height: '2.25rem',
  gap: '0.2rem',
  width: '10rem',
  fontSize: '0.7rem',
  color: colors.black
});

const RejectsButton = styled(Button)<{
  overallStatus: string;
}>(({ overallStatus }) => {
  let backgroundColor;
  let color;
  let border;

  // Set styles based on the status
  switch (overallStatus) {
    case VERIFICATION_STATUS.REJECTED:
      backgroundColor = colors.buttonDisabledText;
      color = colors.white;
      border = `1px solid ${colors.buttonDisabledText}`;
      break;
    case VERIFICATION_STATUS.UPDATE_REQUESTED:
      if (overallStatus === VERIFICATION_STATUS.UPDATE_REQUESTED) {
        backgroundColor = colors.buttonDisabledText;
        color = colors.white;
        border = `1px solid ${colors.buttonDisabledText}`;
      } else {
        backgroundColor = colors.blueChipBg;
        color = colors.black;
        border = `1px solid ${colors.blueBase}`;
      }
      break;
    case VERIFICATION_STATUS.VERIFIED:
      backgroundColor = colors.blueBase;
      color = colors.white;
      border = `1px solid ${colors.blueBase}`;
      break;
    default:
      backgroundColor = colors.blueChipBg;
      color = colors.black;
      border = `1px solid ${colors.blueBase}`;
  }

  return {
    backgroundColor,
    color,
    padding: '6px 12px',
    borderRadius: '0.5rem',
    border,
    textTransform: 'none',
    height: '2.25rem',
    gap: '0.2rem',
    '& .MuiTypography-body1 ': {
      fontSize: '0.8rem'
    },
    // Forcefully remove hover effect
    '&:hover': {
      backgroundColor: `${backgroundColor} !important`, // No change on hover
      color: `${color} !important`, // No change on hover
      boxShadow: 'none !important', // Removes shadow on hover
      border: `${border} !important` // Ensures border remains consistent
    }
  };
});

const DoctorAvatar = styled('img')({
  width: '1.5rem',
  height: '1.5rem',
  marginRight: '10px',
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  flexShrink: 0
});

// Styled container for centering content
const CenteredGrid = styled(Grid)`
  display: flex;
  flex-direction: column;
  align-items: center; // Horizontal alignment
  justify-content: center; // Vertical alignment
  min-height: 100vh; // Ensures full viewport height for centering
`;

const PageContent = styled(Grid)`
  padding-top: 2rem !important;
  padding-right: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ImageModal = styled('div')(() => ({
  '&:hover': {
    cursor: 'pointer'
  }
}));

export {
  AskForUpdateButton,
  VerifyButton,
  IconLeftWrapper,
  IconLeft,
  EmptyStateContainer,
  EmptyMessage,
  getIconColor,
  StatsTop,
  LowerLabel,
  StyledCard,
  CardLabel,
  CardCount,
  CardIcon,
  CardBottom,
  StyledVerificationTitle,
  IndividualGrid,
  DoctorName,
  VerifyText,
  RejectButtonWrapper,
  RejectButton,
  RejectedText,
  TimelineGrid,
  DoctorIdText,
  ChatButtonWrapper,
  RegistrationContent,
  DetailContent,
  BasicDetailsTitle,
  BasicDetailsTitleModal,
  BasicDetailsModalSubTitle,
  BasicDetailsModalContent,
  BasicDetailsGrid,
  DocumentImage,
  AskForUpdateText,
  VerifyUpdateButtons,
  VerifyButtonWrapper,
  DocumentExpiration,
  SectionTitle,
  BasicDetailsTitleModalDegree,
  MedicalDocTitle,
  MedicalDocsModal,
  ExpiryDate,
  RejectProfileWrapper,
  FeedbackLogButton,
  RejectsButton,
  // FeedbackText,
  CenteredGrid,
  PageContent,
  DoctorAvatar,
  ViewText,
  StyledVerifyIconWrapper,
  ImageModal
};
